/**
 * 主站前台 - 功能介绍文章阅读页
 * 路由: /features/:slug
 * 左侧文章目录导航 + 右侧正文
 */
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { GUIDE_ARTICLES } from './guideData';

export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const article = GUIDE_ARTICLES.find(a => a.slug === slug);
  const currentIndex = GUIDE_ARTICLES.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? GUIDE_ARTICLES[currentIndex - 1] : null;
  const nextArticle = currentIndex < GUIDE_ARTICLES.length - 1 ? GUIDE_ARTICLES[currentIndex + 1] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    setSidebarOpen(false);
  }, [slug]);

  if (!article) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '80px' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>📄</p>
          <p style={{ color: '#94a3b8', fontSize: '18px', marginBottom: '24px' }}>文章不存在</p>
          <Link to="/features" style={{ color: '#3b82f6', textDecoration: 'none' }}>← 返回功能介绍</Link>
        </div>
      </div>
    );
  }

  /* ---- styles ---- */
  const articleStyles = `
    .guide-content h2 {
      font-size: 22px; font-weight: 700; color: #f1f5f9;
      margin: 40px 0 16px; padding-bottom: 10px;
      border-bottom: 1px solid rgba(71,85,105,0.4);
    }
    .guide-content h2:first-child { margin-top: 0; }
    .guide-content p {
      font-size: 16px; line-height: 1.9; color: #cbd5e1; margin: 0 0 16px;
    }
    .guide-content strong { color: #f1f5f9; font-weight: 600; }
    .guide-content br { display: block; margin-bottom: 4px; content: ''; }
    @media (max-width: 900px) {
      .guide-sidebar { display: none !important; }
      .guide-main { margin-left: 0 !important; }
    }
  `;

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px' }}>
      <style>{articleStyles}</style>

      {/* Mobile toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{
          display: 'none', position: 'fixed', bottom: '20px', right: '20px',
          zIndex: 100, width: '48px', height: '48px', borderRadius: '50%',
          background: '#3b82f6', color: '#fff', border: 'none', fontSize: '20px',
          cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}
        className="guide-mobile-toggle"
      >
        ☰
      </button>

      <div style={{ display: 'flex', maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>

        {/* Sidebar */}
        <aside className="guide-sidebar" style={{
          width: '280px', flexShrink: 0, position: 'sticky', top: '100px',
          height: 'calc(100vh - 120px)', overflowY: 'auto',
          padding: '24px 0', marginRight: '40px',
          scrollbarWidth: 'thin', scrollbarColor: '#334155 transparent',
        }}>
          <Link to="/features" style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            color: '#64748b', fontSize: '13px', textDecoration: 'none', marginBottom: '20px',
            transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
          >
            ← 全部文章
          </Link>

          <div style={{ fontSize: '11px', color: '#475569', fontWeight: 600, letterSpacing: '2px', marginBottom: '12px', textTransform: 'uppercase' }}>
            系列目录
          </div>

          {GUIDE_ARTICLES.map((a, idx) => {
            const isActive = a.slug === slug;
            return (
              <Link
                key={a.id}
                to={'/features/' + a.slug}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '10px 12px', marginBottom: '2px', borderRadius: '8px',
                  textDecoration: 'none', transition: 'all 0.2s',
                  backgroundColor: isActive ? 'rgba(59,130,246,0.1)' : 'transparent',
                  borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(71,85,105,0.2)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ fontSize: '11px', color: isActive ? '#3b82f6' : '#475569', fontWeight: 600, flexShrink: 0, marginTop: '2px' }}>
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span style={{ fontSize: '13px', color: isActive ? '#e2e8f0' : '#94a3b8', lineHeight: 1.4, fontWeight: isActive ? 600 : 400 }}>
                  {a.title.split('：')[0].split('——')[0]}
                </span>
              </Link>
            );
          })}
        </aside>

        {/* Main Content */}
        <main className="guide-main" style={{ flex: 1, minWidth: 0, padding: '24px 0 80px' }}>
          {/* Breadcrumb */}
          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '32px' }}>
            <Link to="/features" style={{ color: '#64748b', textDecoration: 'none' }}>功能介绍</Link>
            <span style={{ margin: '0 8px' }}>/</span>
            <span style={{ color: '#94a3b8' }}>第 {article.id} 篇</span>
          </div>

          {/* Article Header */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '40px' }}>{article.icon}</span>
              <div>
                <div style={{ fontSize: '12px', color: '#60a5fa', fontWeight: 600, letterSpacing: '1px', marginBottom: '4px' }}>
                  第 {article.id} 篇 · {article.readTime}阅读
                </div>
              </div>
            </div>
            <h1 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 800, color: '#f1f5f9', lineHeight: 1.3, marginBottom: '12px' }}>
              {article.title}
            </h1>
            <p style={{ fontSize: '17px', color: '#94a3b8', lineHeight: 1.6 }}>
              {article.subtitle}
            </p>
          </div>

          {/* Divider */}
          <div style={{ height: '1px', background: 'linear-gradient(90deg, #3b82f6, transparent)', marginBottom: '40px' }} />

          {/* Article Body */}
          <div
            className="guide-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Navigation */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: '16px',
            marginTop: '60px', paddingTop: '32px', borderTop: '1px solid rgba(71,85,105,0.4)',
          }}>
            {prevArticle ? (
              <Link to={'/features/' + prevArticle.slug} style={{
                flex: 1, padding: '20px', borderRadius: '12px', textDecoration: 'none',
                background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(71,85,105,0.3)',
                transition: 'all 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(71,85,105,0.3)'; }}
              >
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>← 上一篇</div>
                <div style={{ fontSize: '15px', color: '#e2e8f0', fontWeight: 600 }}>
                  {prevArticle.icon} {prevArticle.title.split('：')[0].split('——')[0]}
                </div>
              </Link>
            ) : <div style={{ flex: 1 }} />}

            {nextArticle ? (
              <Link to={'/features/' + nextArticle.slug} style={{
                flex: 1, padding: '20px', borderRadius: '12px', textDecoration: 'none',
                background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(71,85,105,0.3)',
                textAlign: 'right', transition: 'all 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(71,85,105,0.3)'; }}
              >
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>下一篇 →</div>
                <div style={{ fontSize: '15px', color: '#e2e8f0', fontWeight: 600 }}>
                  {nextArticle.icon} {nextArticle.title.split('：')[0].split('——')[0]}
                </div>
              </Link>
            ) : (
              <Link to="/register" style={{
                flex: 1, padding: '20px', borderRadius: '12px', textDecoration: 'none',
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(37,99,235,0.1))',
                border: '1px solid rgba(59,130,246,0.3)', textAlign: 'right',
              }}>
                <div style={{ fontSize: '12px', color: '#60a5fa', marginBottom: '6px' }}>系列完结</div>
                <div style={{ fontSize: '15px', color: '#e2e8f0', fontWeight: 600 }}>🚀 立即免费体验 NCB</div>
              </Link>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
